import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BASE_URL, TAGS } from "../constants";
import { getSupabaseSession } from "src/supabase/supabaseClient";
import {
  PatientProfile,
  States,
  FilesFromStorage,
  GetAnswersArgs,
  Answer,
  ProfileWithRoles,
  EmployeesView,
} from "../types";

export const api = createApi({
  reducerPath: "api",
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    mode: "cors",
    prepareHeaders: async (headers, { endpoint }) => {
      const session = await getSupabaseSession();

      if (session?.access_token != null) {
        headers.set("Authorization", `Bearer ${session.access_token}`);
      }

      headers.set("Content-Type", "application/json");

      if (["uploadFileToBucket", "uploadFile"].includes(endpoint)) {
        headers.delete("Content-Type");
      }

      return headers;
    },
  }),
  tagTypes: Object.values(TAGS),
  endpoints: (builder) => ({
    //current user
    getMe: builder.query<ProfileWithRoles, void>({
      query: () => ({
        method: "GET",
        url: "/api/profiles/employee/me",
      }),
      providesTags: [TAGS.PROFILES],
    }),
    /**
     * Answers
     */

    getAnswers: builder.query<Answer[], GetAnswersArgs>({
      query: (filter) => ({
        url: "/api/answers",
        method: "GET",
        params: {
          ...filter,
        },
      }),
    }),

    //patients
    getPatientProfiles: builder.query<
      {
        data: PatientProfile[];
        count: number;
      },
      {
        firstName?: string;
        lastName?: string;
        email?: string;
        phoneNumber?: string;
        from: number;
        to: number;
      }
    >({
      query: (params) => ({
        method: "GET",
        url: "/api/patients",
        params,
      }),
    }),
    getPatientById: builder.query<PatientProfile, number>({
      query: (patientId) => ({
        method: "GET",
        url: `/api/patients/${patientId}`,
      }),
      providesTags: (_returns, _error, args) => [
        { type: TAGS.PATIENT, id: args },
      ],
    }),
    generateQualityReviewLink: builder.mutation<
      string,
      { patientEmail: string; redirectTo: string }
    >({
      query: (body) => ({
        method: "POST",
        url: "/api/patients/quality-review",
        body,
        responseHandler: "text",
      }),
    }),
    //bucket
    getPatientIdentification: builder.query<
      FilesFromStorage[],
      { profileId: string; folderName: string }
    >({
      query: ({ profileId, folderName }) => ({
        method: "GET",
        url: `/api/bucket/${folderName}/patient/${profileId}`,
      }),
      providesTags: [TAGS.BUCKET],
    }),
    //providers
    getEmployees: builder.query<
      EmployeesView[],
      void | {
        role?: string;
      }
    >({
      query: (params) => ({
        method: "GET",
        url: "/api/profiles",
        params: params ?? undefined,
      }),
      providesTags: [TAGS.PROFILES],
    }),
    updateProviderProfileInfo: builder.mutation({
      query: ({ id, ...rest }) => ({
        method: "PUT",
        url: `api/profiles/${id}`,
        body: rest,
      }),
      invalidatesTags: [TAGS.PROVIDERS],
    }),
    updateProvider: builder.mutation({
      query: ({ id, ...rest }) => ({
        method: "PUT",
        url: `api/providers/${id}`,
        body: rest,
      }),
    }),
    //states
    getStates: builder.query<States[], void>({
      query: () => ({
        method: "GET",
        url: "/api/states",
      }),
      providesTags: [TAGS.STATES],
    }),
    updateState: builder.mutation<States, Partial<States>>({
      query: ({ id, ...rest }) => ({
        method: "PUT",
        url: `/api/states/${id}`,
        body: rest,
      }),
      invalidatesTags: [TAGS.STATES],
    }),
  }),
});

export const {
  useGetEmployeesQuery,
  useUpdateProviderProfileInfoMutation,
  useUpdateProviderMutation,
  useGetPatientProfilesQuery,
  useGetStatesQuery,
  useUpdateStateMutation,
  useGetMeQuery,
  useGetPatientByIdQuery,
  useGenerateQualityReviewLinkMutation,
  useGetPatientIdentificationQuery,
  useGetAnswersQuery,
} = api;
